import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Drawer,
  MenuItem,
  MenuList,
  Toolbar,
  Typography,
  Paper,
  Popper,
  Popover,
} from '@mui/material';
import {
  GpsFixed as TargetIcon,
  InfoOutlined as InfoIcon,
  SettingsOutlined as SettingsOutlinedIcon,
} from '@mui/icons-material';
import { PiFlowerLotus } from "react-icons/pi";
import { LuScrollText } from "react-icons/lu";
import { useLocation, useNavigate } from 'react-router-dom';

const DRAWER_WIDTH = 280;

// Wrapper component to fix React 19 compatibility with react-icons
const LotusIcon: React.FC = () => {
  return <>{PiFlowerLotus({ size: 24 })}</>;
};

// Wrapper component for Lucide scroll-text icon
const ScrollIcon: React.FC = () => {
  return <>{LuScrollText({ size: 24 })}</>;
};

interface SidebarItem {
  text: string;
  icon: React.ReactNode;
  path: string;
}

const sidebarItems: SidebarItem[] = [
  {
    text: 'Today',
    icon: <LotusIcon />,
    path: '/today/log-habits',
  },
  {
    text: 'History',
    icon: <ScrollIcon />,
    path: '/history/stats',
  },
  {
    text: 'Upcoming',
    icon: <TargetIcon />,
    path: '/upcoming',
  },
];

interface SidebarProps {
  variant?: 'permanent' | 'persistent' | 'temporary';
  open?: boolean;
  onClose?: () => void;
  onOpenHint?: () => void;
  onOpenViewTags?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  variant = 'permanent',
  open,
  onClose,
  onOpenHint,
  onOpenViewTags,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [settingsAnchorEl, setSettingsAnchorEl] = useState<HTMLElement | null>(null);
  const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLElement | null>(null);
  const infoCloseTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (infoCloseTimeoutRef.current !== null) {
        window.clearTimeout(infoCloseTimeoutRef.current);
      }
    };
  }, []);

  const cancelInfoMenuClose = () => {
    if (infoCloseTimeoutRef.current !== null) {
      window.clearTimeout(infoCloseTimeoutRef.current);
      infoCloseTimeoutRef.current = null;
    }
  };

  const openInfoMenu = (event: React.MouseEvent<HTMLElement> | React.FocusEvent<HTMLElement>) => {
    cancelInfoMenuClose();
    setInfoAnchorEl(event.currentTarget);
  };

  const scheduleInfoMenuClose = () => {
    cancelInfoMenuClose();
    infoCloseTimeoutRef.current = window.setTimeout(() => {
      setInfoAnchorEl(null);
      infoCloseTimeoutRef.current = null;
    }, 180);
  };

  const closeInfoMenu = () => {
    cancelInfoMenuClose();
    setInfoAnchorEl(null);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (variant === 'temporary' && onClose) {
      onClose();
    }
  };

  const drawerContent = (
    <Box sx={{ overflow: 'auto', height: '100%', backgroundColor: '#32CA1D', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', flex: 1, position: 'relative' }}
          >
            <img 
              src="/logo192.png" 
              alt="AirMask Logo" 
              style={{ width: 28, height: 28, marginRight: 10 }}
            />
            <Typography 
              variant="h6" 
              noWrap 
              component="div" 
              sx={{ 
                fontWeight: 700,
                color: 'primary.contrastText',
                fontSize: '1.1rem'
              }}
            >
              AirMask
            </Typography>
            <IconButton
              size="small"
              onClick={(event) => setSettingsAnchorEl(event.currentTarget)}
              sx={{
                position: 'absolute',
                right: 0,
                top: 2,
                color: 'primary.contrastText',
                opacity: 0.8,
                p: 0,
                width: 27,
                height: 27,
                transition: 'background-color 0.2s ease, opacity 0.2s ease',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  opacity: 1,
                },
              }}
              aria-label="Settings"
            >
              <SettingsOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>
      </Toolbar>

      <Popover
        open={Boolean(settingsAnchorEl)}
        anchorEl={settingsAnchorEl}
        onClose={() => setSettingsAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            px: 1.5,
            py: 1,
            bgcolor: 'rgba(20, 20, 20, 0.95)',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            borderRadius: 1,
          },
        }}
      >
        <Typography variant="body2">
          settings will be added with a later release.
        </Typography>
      </Popover>
      
      <Box sx={{ px: 3, pt: 2 }}>
        <Typography 
          variant="overline" 
          sx={{ 
            display: 'block',
            color: 'primary.contrastText',
            fontWeight: 600,
            fontSize: '0.75rem',
            letterSpacing: 1,
            mb: 2,
            px: 1,
            opacity: 0.8
          }}
        >
        </Typography>
        
        <List sx={{ p: 0 }}>
          {sidebarItems.map((item) => {
            // For /today/* and /history/* paths, match any route under that section
            const isSelected = item.path.startsWith('/today') || item.path.startsWith('/history')
              ? location.pathname.startsWith(item.path.split('/')[1] === 'today' ? '/today' : '/history')
              : location.pathname === item.path;
            
            return (
              <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => handleNavigation(item.path)}
                  selected={isSelected}
                  sx={{
                    borderRadius: 1.5,
                    minHeight: 44,
                    px: 2,
                    py: 1.5,
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'primary.contrastText',
                      backdropFilter: 'blur(10px)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                      },
                      '& .MuiListItemIcon-root': {
                        color: 'primary.contrastText',
                      },
                    },
                    '&:hover': {
                      backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 36,
                      color: isSelected ? 'inherit' : 'primary.contrastText',
                      opacity: isSelected ? 1 : 0.8,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.text}
                    primaryTypographyProps={{
                      fontWeight: isSelected ? 600 : 500,
                      fontSize: '0.9rem',
                      color: 'primary.contrastText',
                    }}
                    sx={{ opacity: isSelected ? 1 : 0.9 }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      <Box sx={{ px: 3, pb: 2, mt: 'auto' }}>
        <List sx={{ p: 0 }}>
          <ListItem disablePadding>
            <ListItemButton
              onMouseEnter={openInfoMenu}
              onMouseLeave={scheduleInfoMenuClose}
              onFocus={openInfoMenu}
              onBlur={scheduleInfoMenuClose}
              sx={{
                borderRadius: 1.5,
                minHeight: 44,
                px: 2,
                py: 1.5,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
              aria-label="AirMask info"
            >
              <ListItemIcon
                sx={{
                  minWidth: 36,
                  color: 'primary.contrastText',
                  opacity: 0.8,
                }}
              >
                {/* I want to later display settings here instead of info. */}
                <InfoIcon />
              </ListItemIcon>
            </ListItemButton>
          </ListItem>
        </List>

        <Popper
          open={Boolean(infoAnchorEl)}
          anchorEl={infoAnchorEl}
          placement="top-end"
          disablePortal
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 6],
              },
            },
          ]}
          sx={{ zIndex: 1301 }}
        >
          <Paper
            elevation={4}
            onMouseEnter={cancelInfoMenuClose}
            onMouseLeave={scheduleInfoMenuClose}
            sx={{
              minWidth: 190,
              mt: -0.5,
            }}
          >
            <MenuList autoFocusItem={false}>
              <MenuItem
                onClick={() => {
                  closeInfoMenu();
                  if (onOpenHint) {
                    onOpenHint();
                  }
                  if (variant === 'temporary' && onClose) {
                    onClose();
                  }
                }}
              >
                <ListItemText>Habit Categories</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  closeInfoMenu();
                  if (onOpenViewTags) {
                    onOpenViewTags();
                  }
                  if (variant === 'temporary' && onClose) {
                    onClose();
                  }
                }}
              >
                <ListItemText>View Tags</ListItemText>
              </MenuItem>
            </MenuList>
          </Paper>
        </Popper>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={variant}
      open={variant === 'temporary' ? open : true}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          border: 'none',
          backgroundColor: '#32CA1D',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
export { DRAWER_WIDTH };